"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "framer-motion";
import { Brain, ChevronLeft, ChevronRight, Command, Github, Keyboard, Menu, Rocket, ShieldCheck, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import { useScrollSpy } from "../hooks/useScrollSpy";
const sections = [{
  id: "what-is-cmaes",
  label: "Basics"
}, {
  id: "no-gradients",
  label: "Examples"
}, {
  id: "wing-walkthrough",
  label: "Wings"
}, {
  id: "engines",
  label: "Engines"
}, {
  id: "live-demo",
  label: "Demo"
}, {
  id: "internals",
  label: "3D Space"
}, {
  id: "robotics-flagships",
  label: "Robotics"
}, {
  id: "hpo",
  label: "HPO"
}, {
  id: "technical-addendum",
  label: "Math"
}];
export function Navbar() {
  const $ = _c(92);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = id => pathname === "/" ? `#${id}` : `/#${id}`;
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const getHref = t0;
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shortcutsModalOpen, setShortcutsModalOpen] = useState(false);
  const progressBarRef = useRef(null);
  const dockRef = useRef(null);
  const closeBtnRef = useRef(null);
  const mobileMenuOpenBtnRef = useRef(null);
  const mobileMenuCloseBtnRef = useRef(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = sections.map(_temp);
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeId = useScrollSpy(t1);
  let t2;
  let t3;
  if ($[3] !== activeId) {
    t2 = () => {
      const dock = dockRef.current;
      if (!dock || !activeId) {
        return;
      }
      const pill = dock.querySelector(`a[href="#${activeId}"]`);
      if (!pill) {
        return;
      }
      const target = pill.offsetLeft - (dock.clientWidth - pill.clientWidth) / 2;
      dock.scrollTo({
        left: Math.max(0, target),
        behavior: "smooth"
      });
    };
    t3 = [activeId];
    $[3] = activeId;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      let ticking = false;
      const handler = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            setScrolled(_temp2);
            const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (totalHeight > 0 && progressBarRef.current) {
              const progress = Math.min(1, Math.max(0, window.scrollY / totalHeight));
              progressBarRef.current.style.transform = `scaleX(${progress})`;
            }
            ticking = false;
          });
          ticking = true;
        }
      };
      handler();
      window.addEventListener("scroll", handler, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handler);
    };
    t5 = [];
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== activeId) {
    t6 = e => {
      const target_0 = e.target as HTMLElement | null;
      if (target_0 && (target_0.tagName === "INPUT" || target_0.tagName === "TEXTAREA" || target_0.tagName === "SELECT" || target_0.isContentEditable)) {
        return;
      }
      if (e.key === "?" || e.key === "/" && !e.shiftKey) {
        e.preventDefault();
        setShortcutsModalOpen(_temp3);
      } else {
        if (e.key === "Escape") {
          setShortcutsModalOpen(false);
          setMobileMenuOpen(false);
        } else {
          if (e.key === "j" || e.key === "J") {
            e.preventDefault();
            const currentIdx = sections.findIndex(s_0 => s_0.id === activeId);
            const nextIdx = Math.min(sections.length - 1, currentIdx + 1);
            const nextEl = document.getElementById(sections[nextIdx].id);
            if (nextEl) {
              nextEl.scrollIntoView({
                behavior: "smooth"
              });
            }
          } else {
            if (e.key === "k" || e.key === "K") {
              e.preventDefault();
              const currentIdx_0 = sections.findIndex(s_1 => s_1.id === activeId);
              const prevIdx = Math.max(0, currentIdx_0 - 1);
              const prevEl = document.getElementById(sections[prevIdx].id);
              if (prevEl) {
                prevEl.scrollIntoView({
                  behavior: "smooth"
                });
              }
            } else {
              if (e.key === "t" || e.key === "T") {
                e.preventDefault();
                window.scrollTo({
                  top: 0,
                  behavior: "smooth"
                });
              } else {
                if (e.key === "d" || e.key === "D") {
                  e.preventDefault();
                  const el = document.getElementById("live-demo");
                  if (el) {
                    el.scrollIntoView({
                      behavior: "smooth"
                    });
                  }
                } else {
                  if (e.key === "m" || e.key === "M") {
                    e.preventDefault();
                    const el_0 = document.getElementById("technical-addendum");
                    if (el_0) {
                      el_0.scrollIntoView({
                        behavior: "smooth"
                      });
                    }
                  }
                }
              }
            }
          }
        }
      }
    };
    $[8] = activeId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleKeyDown = t6;
  let t7;
  let t8;
  if ($[10] !== handleKeyDown) {
    t7 = () => {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t8 = [handleKeyDown];
    $[10] = handleKeyDown;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[13] !== mobileMenuOpen || $[14] !== shortcutsModalOpen) {
    t9 = () => {
      const open = mobileMenuOpen || shortcutsModalOpen;
      if (!open) {
        return;
      }
      const body = document.body;
      const previousOverflow = body.style.overflow;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      body.style.overflow = "hidden";
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
      return () => {
        if (previousOverflow) {
          body.style.overflow = previousOverflow;
        } else {
          body.style.removeProperty("overflow");
        }
        body.style.removeProperty("padding-right");
      };
    };
    t10 = [mobileMenuOpen, shortcutsModalOpen];
    $[13] = mobileMenuOpen;
    $[14] = shortcutsModalOpen;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[17] !== shortcutsModalOpen) {
    t11 = () => {
      if (!shortcutsModalOpen) {
        return;
      }
      const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      closeBtnRef.current?.focus();
      return () => previouslyFocused?.focus();
    };
    t12 = [shortcutsModalOpen];
    $[17] = shortcutsModalOpen;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[20] !== mobileMenuOpen) {
    t13 = () => {
      if (!mobileMenuOpen) {
        return;
      }
      const openBtn = mobileMenuOpenBtnRef.current;
      const rafId = requestAnimationFrame(() => mobileMenuCloseBtnRef.current?.focus());
      return () => {
        cancelAnimationFrame(rafId);
        openBtn?.focus();
      };
    };
    t14 = [mobileMenuOpen];
    $[20] = mobileMenuOpen;
    $[21] = t13;
    $[22] = t14;
  } else {
    t13 = $[21];
    t14 = $[22];
  }
  useEffect(t13, t14);
  const trapDialogTab = _temp5;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="fixed top-0 inset-x-0 h-1 z-[100] bg-slate-950/40 pointer-events-none"><div ref={progressBarRef} className="h-full w-full origin-left bg-gradient-to-r from-sky-400 via-cyan-400 to-indigo-500 shadow-[0_0_10px_rgba(56,189,248,0.8)]" style={{
        transform: "scaleX(0)"
      }} /></div>;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = `fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,padding] duration-500 hidden lg:block ${scrolled ? "bg-[#020617]/85 backdrop-blur-xl border-b border-white/10 py-3.5 shadow-2xl shadow-black/50" : "bg-transparent border-b border-transparent py-5"}`;
  let t17;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="relative h-10 w-10 rounded-xl bg-gradient-to-br from-sky-500/20 to-indigo-500/20 border border-white/10 flex items-center justify-center shadow-inner group-hover:shadow-glow-sm transition-[transform,box-shadow] duration-300 group-hover:scale-105"><Brain className="h-5 w-5 text-sky-300" /></div>;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Link href="/" className="flex items-center gap-3 group">{t17}<div className="flex flex-col"><span className="text-sm font-bold tracking-tight text-slate-100 font-display group-hover:text-white transition-colors">CMA-ES Explainer</span><span className="text-[0.65rem] text-slate-400 font-medium tracking-wider uppercase opacity-80 group-hover:opacity-100 transition-opacity">Interactive Guide</span></div></Link>;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== activeId || $[27] !== getHref || $[28] !== pathname) {
    t19 = sections.map(s_2 => <a key={s_2.id} href={getHref(s_2.id)} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-[background-color,color,box-shadow] duration-200 ${pathname === "/" && activeId === s_2.id ? "bg-sky-500 text-white shadow-glow-sm" : "text-slate-400 hover:text-slate-200 hover:bg-white/5"}`}>{s_2.label}</a>);
    $[26] = activeId;
    $[27] = getHref;
    $[28] = pathname;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="w-[1px] h-4 bg-white/10 mx-1 shrink-0" />;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  const t21 = `px-2.5 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wider transition-colors ${pathname === "/humanoid" ? "bg-cyan-500/25 text-cyan-200 border border-cyan-400/40" : "text-cyan-400/80 hover:text-cyan-300 hover:bg-cyan-500/10"}`;
  let t22;
  if ($[31] !== t21) {
    t22 = <Link href="/humanoid" className={t21}>G1</Link>;
    $[31] = t21;
    $[32] = t22;
  } else {
    t22 = $[32];
  }
  const t23 = `px-2.5 py-1 rounded-full text-[0.7rem] font-bold uppercase tracking-wider transition-colors ${pathname === "/arm" ? "bg-orange-500/25 text-orange-200 border border-orange-400/40" : "text-orange-400/80 hover:text-orange-300 hover:bg-orange-500/10"}`;
  let t24;
  if ($[33] !== t23) {
    t24 = <Link href="/arm" className={t23}>iiwa</Link>;
    $[33] = t23;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t19 || $[36] !== t22 || $[37] !== t24) {
    t25 = <div className="flex items-center gap-1 rounded-full bg-slate-950/60 p-1.5 border border-white/10 backdrop-blur-md shadow-inner">{t19}{t20}{t22}{t24}</div>;
    $[35] = t19;
    $[36] = t22;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => setShortcutsModalOpen(true);
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <button onClick={t26} title="Keyboard shortcuts (Press ?)" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-mono text-slate-400 hover:text-slate-200 bg-slate-900/60 border border-white/10 hover:border-white/20 transition-[background-color,color,border-color]"><Keyboard className="h-3.5 w-3.5" /><span className="text-[0.65rem] bg-white/10 px-1 py-0.5 rounded font-mono">?</span></button>;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <a href="https://github.com/Dicklesworthstone/cmaes_explainer" target="_blank" rel="noreferrer" className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-400 hover:text-white transition-colors"><Github className="h-4 w-4" /><span className="hidden xl:inline">Source</span></a>;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  const t29 = getHref("live-demo");
  let t30;
  let t31;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Rocket className="h-3.5 w-3.5" />;
    t31 = <span>Run Demo</span>;
    $[42] = t30;
    $[43] = t31;
  } else {
    t30 = $[42];
    t31 = $[43];
  }
  let t32;
  if ($[44] !== t29) {
    t32 = <div className="flex items-center gap-3">{t27}{t28}<a href={t29} className="flex flex-wrap items-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-600 px-4 py-2 text-xs font-bold text-white shadow-glow-sm transition-[background-image,transform] hover:scale-105">{t30}{t31}</a></div>;
    $[44] = t29;
    $[45] = t32;
  } else {
    t32 = $[45];
  }
  let t33;
  if ($[46] !== t25 || $[47] !== t32) {
    t33 = <nav className="mx-auto flex max-w-7xl items-center justify-between px-8">{t18}{t25}{t32}</nav>;
    $[46] = t25;
    $[47] = t32;
    $[48] = t33;
  } else {
    t33 = $[48];
  }
  let t34;
  if ($[49] !== t16 || $[50] !== t33) {
    t34 = <header className={t16}>{t33}</header>;
    $[49] = t16;
    $[50] = t33;
    $[51] = t34;
  } else {
    t34 = $[51];
  }
  let t35;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <Link href="/" className="flex items-center gap-2.5"><div className="h-8 w-8 rounded-lg bg-sky-500/20 border border-white/10 flex items-center justify-center"><Brain className="h-4 w-4 text-sky-300" /></div><span className="text-sm font-bold text-white font-display">CMA-ES Explainer</span></Link>;
    $[52] = t35;
  } else {
    t35 = $[52];
  }
  let t36;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = () => setShortcutsModalOpen(true);
    $[53] = t36;
  } else {
    t36 = $[53];
  }
  let t37;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <button aria-label="Keyboard Shortcuts" onClick={t36} className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 border border-white/10 text-slate-300"><Keyboard className="h-4 w-4" /></button>;
    $[54] = t37;
  } else {
    t37 = $[54];
  }
  let t38;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = () => setMobileMenuOpen(true);
    $[55] = t38;
  } else {
    t38 = $[55];
  }
  let t39;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="fixed top-0 inset-x-0 z-40 flex lg:hidden items-center justify-between px-4 py-1.5 bg-[#020617]/90 backdrop-blur-lg border-b border-white/10">{t35}<div className="flex items-center gap-1.5">{t37}<button ref={mobileMenuOpenBtnRef} aria-label="Open navigation menu" onClick={t38} className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 border border-white/10 text-slate-300 hover:text-white"><Menu className="h-5 w-5" /></button></div></div>;
    $[56] = t39;
  } else {
    t39 = $[56];
  }
  let t40;
  if ($[57] !== activeId || $[58] !== getHref || $[59] !== pathname) {
    t40 = sections.map(s_3 => <a key={s_3.id} href={getHref(s_3.id)} className={`flex h-8 items-center px-2.5 rounded-full text-[0.7rem] font-semibold whitespace-nowrap transition-[background-color,color,box-shadow] ${pathname === "/" && activeId === s_3.id ? "bg-sky-500 text-white shadow-glow-sm" : "text-slate-400 hover:text-slate-200"}`}>{s_3.label}</a>);
    $[57] = activeId;
    $[58] = getHref;
    $[59] = pathname;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  let t41;
  if ($[61] !== t40) {
    t41 = <div ref={dockRef} className="pointer-events-auto flex items-center gap-1 rounded-full bg-[#030712]/90 backdrop-blur-2xl border border-white/15 p-1 shadow-[0_15px_35px_rgba(0,0,0,0.8)] flex-1 min-w-0 overflow-x-auto no-scrollbar dock-fade">{t40}</div>;
    $[61] = t40;
    $[62] = t41;
  } else {
    t41 = $[62];
  }
  let t42;
  if ($[63] !== activeId) {
    t42 = s_4 => s_4.id === activeId;
    $[63] = activeId;
    $[64] = t42;
  } else {
    t42 = $[64];
  }
  const t43 = Math.max(1, sections.findIndex(t42) + 1);
  let t44;
  if ($[65] !== t43) {
    t44 = <span>{t43}</span>;
    $[65] = t43;
    $[66] = t44;
  } else {
    t44 = $[66];
  }
  let t45;
  let t46;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <span className="text-slate-500">/</span>;
    t46 = <span>{sections.length}</span>;
    $[67] = t45;
    $[68] = t46;
  } else {
    t45 = $[67];
    t46 = $[68];
  }
  let t47;
  if ($[69] !== t44) {
    t47 = <div className="pointer-events-auto flex h-8 shrink-0 items-center gap-1 rounded-full bg-[#030712]/90 px-2.5 backdrop-blur-2xl border border-white/15 font-mono text-[0.7rem] text-slate-300">{t44}{t45}{t46}</div>;
    $[69] = t44;
    $[70] = t47;
  } else {
    t47 = $[70];
  }
  let t48;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = () => setMobileMenuOpen(true);
    $[71] = t48;
  } else {
    t48 = $[71];
  }
  let t49;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <button aria-label="Open full menu" onClick={t48} className="pointer-events-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#030712]/90 backdrop-blur-2xl border border-white/15 text-slate-300 shadow-[0_15px_35px_rgba(0,0,0,0.8)]"><Menu className="h-4 w-4" /></button>;
    $[72] = t49;
  } else {
    t49 = $[72];
  }
  let t50;
  if ($[73] !== t41 || $[74] !== t47) {
    t50 = <div className="fixed bottom-5 inset-x-0 z-40 flex lg:hidden items-center gap-2 pointer-events-none px-4">{t41}{t47}{t49}</div>;
    $[73] = t41;
    $[74] = t47;
    $[75] = t50;
  } else {
    t50 = $[75];
  }
  let t51;
  if ($[76] !== shortcutsModalOpen) {
    t51 = shortcutsModalOpen && <div role="dialog" aria-modal="true" aria-label="Keyboard shortcuts" onKeyDown={trapDialogTab} className="fixed inset-0 z-[70] flex items-center justify-center p-4"><motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setShortcutsModalOpen(false)} className="absolute inset-0 bg-black/80 backdrop-blur-md" /><motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 15
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 15
      }} className="relative w-full max-w-md rounded-3xl border border-white/15 bg-slate-950 p-6 shadow-2xl space-y-5"><div className="flex items-center justify-between border-b border-white/10 pb-4"><div className="flex items-center gap-2.5"><div className="p-2 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20"><Keyboard className="h-5 w-5" /></div><div><h3 className="text-base font-bold text-white font-display">Keyboard Shortcuts</h3><p className="text-xs text-slate-400">Navigate the interactive explainer with speed</p></div></div><button ref={closeBtnRef} onClick={() => setShortcutsModalOpen(false)} aria-label="Close keyboard shortcuts" className="p-1.5 rounded-lg text-slate-400 hover:text-white bg-white/5 hover:bg-white/10"><X className="h-5 w-5" /></button></div><div className="space-y-2.5 text-xs">{[{
            keys: ["J"],
            desc: "Jump to next chapter"
          }, {
            keys: ["K"],
            desc: "Jump to previous chapter"
          }, {
            keys: ["D"],
            desc: "Launch live WebAssembly benchmark demo"
          }, {
            keys: ["M"],
            desc: "Jump to Mathematical Addendum"
          }, {
            keys: ["T"],
            desc: "Scroll smoothly to top of page"
          }, {
            keys: ["?"],
            desc: "Toggle this keyboard shortcuts dialog"
          }, {
            keys: ["Esc"],
            desc: "Close open dialog or drawer"
          }].map(_temp7)}</div><div className="pt-2 text-center text-[0.7rem] text-slate-500">Tip: Press{" "}<kbd className="px-1.5 py-0.5 rounded bg-slate-900 border border-white/10 font-mono text-slate-400">Esc</kbd>{" "}anytime to dismiss.</div></motion.div></div>;
    $[76] = shortcutsModalOpen;
    $[77] = t51;
  } else {
    t51 = $[77];
  }
  let t52;
  if ($[78] !== t51) {
    t52 = <AnimatePresence>{t51}</AnimatePresence>;
    $[78] = t51;
    $[79] = t52;
  } else {
    t52 = $[79];
  }
  let t53;
  if ($[80] !== activeId || $[81] !== getHref || $[82] !== mobileMenuOpen || $[83] !== pathname) {
    t53 = mobileMenuOpen && <motion.div role="dialog" aria-modal="true" aria-label="Navigation menu" onKeyDown={trapDialogTab} initial={{
      opacity: 0,
      y: "100%"
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: "100%"
    }} transition={{
      type: "spring",
      damping: 25,
      stiffness: 200
    }} className="fixed inset-0 z-[60] bg-[#020617] lg:hidden flex flex-col"><div className="flex items-center justify-between px-6 py-4 border-b border-white/10"><div className="flex items-center gap-2.5"><Brain className="h-5 w-5 text-sky-400" /><span className="text-lg font-bold font-display text-white">Table of Contents</span></div><button ref={mobileMenuCloseBtnRef} aria-label="Close navigation menu" onClick={() => setMobileMenuOpen(false)} className="flex min-h-10 min-w-10 items-center justify-center rounded-full bg-white/5 text-slate-300 hover:bg-white/10"><X className="h-6 w-6" /></button></div><div className="flex-1 overflow-y-auto p-6 space-y-2.5"><div className="grid grid-cols-2 gap-2 mb-4"><Link href="/humanoid" onClick={() => setMobileMenuOpen(false)} className="rounded-2xl border border-cyan-400/30 bg-cyan-950/40 p-3.5 text-left"><span className="text-[0.65rem] font-bold uppercase tracking-wider text-cyan-300 block">Flagship 1</span><span className="text-sm font-bold text-white block mt-0.5">G1 Walking</span><span className="text-[0.7rem] text-cyan-200/70 block mt-1">5,040-D Whole-Body</span></Link><Link href="/arm" onClick={() => setMobileMenuOpen(false)} className="rounded-2xl border border-orange-400/30 bg-orange-950/40 p-3.5 text-left"><span className="text-[0.65rem] font-bold uppercase tracking-wider text-orange-300 block">Flagship 2</span><span className="text-sm font-bold text-white block mt-0.5">iiwa 7 Arm</span><span className="text-[0.7rem] text-orange-200/70 block mt-1">128-D Household</span></Link></div>{sections.map((s_5, i) => <motion.a initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: i * 0.04
        }} key={s_5.id} href={getHref(s_5.id)} onClick={() => setMobileMenuOpen(false)} className={`flex items-center justify-between p-4 rounded-2xl border transition-colors ${pathname === "/" && activeId === s_5.id ? "bg-sky-500/15 border-sky-500/40 text-white shadow-glow-sm" : "bg-white/5 border-white/5 text-slate-300 active:bg-white/10"}`}><div className="flex items-center gap-3"><span className="text-xs font-mono font-bold text-sky-400 w-5">0{i + 1}</span><span className="font-semibold text-slate-200">{s_5.label}</span></div><ChevronRight className="h-4 w-4 text-slate-500" /></motion.a>)}<div className="mt-8 p-6 rounded-3xl bg-gradient-to-br from-sky-900/20 via-indigo-900/20 to-purple-900/20 border border-sky-500/20 shadow-2xl"><div className="flex items-center gap-3 mb-3"><Rocket className="h-5 w-5 text-sky-400" /><span className="font-bold text-white font-display">Run the Live WASM Demo</span></div><p className="text-xs text-slate-400 mb-4 leading-relaxed">Execute SIMD WebAssembly optimization kernels with real-time 2D and 3D phase-space visualization directly in your browser.</p><a href="#live-demo" onClick={() => setMobileMenuOpen(false)} className="block w-full py-3.5 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 text-center text-sm font-bold text-white shadow-glow-sm active:scale-95 transition-transform">Launch Playground</a></div></div></motion.div>;
    $[80] = activeId;
    $[81] = getHref;
    $[82] = mobileMenuOpen;
    $[83] = pathname;
    $[84] = t53;
  } else {
    t53 = $[84];
  }
  let t54;
  if ($[85] !== t53) {
    t54 = <AnimatePresence>{t53}</AnimatePresence>;
    $[85] = t53;
    $[86] = t54;
  } else {
    t54 = $[86];
  }
  let t55;
  if ($[87] !== t34 || $[88] !== t50 || $[89] !== t52 || $[90] !== t54) {
    t55 = <>{t15}{t34}{t39}{t50}{t52}{t54}</>;
    $[87] = t34;
    $[88] = t50;
    $[89] = t52;
    $[90] = t54;
    $[91] = t55;
  } else {
    t55 = $[91];
  }
  return t55;
}
function _temp7(item, idx) {
  return <div key={idx} className="flex items-center justify-between p-2.5 rounded-xl bg-white/[0.03] border border-white/5"><span className="text-slate-300 font-medium">{item.desc}</span><div className="flex items-center gap-1">{item.keys.map(_temp6)}</div></div>;
}
function _temp6(k) {
  return <kbd key={k} className="px-2 py-1 rounded bg-slate-900 border border-white/15 font-mono text-[0.7rem] text-sky-300 font-bold shadow-inner">{k}</kbd>;
}
function _temp5(e_0) {
  if (e_0.key !== "Tab") {
    return;
  }
  const dialog = e_0.currentTarget;
  const focusables = Array.from(dialog.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex=\"-1\"])")).filter(_temp4);
  if (focusables.length === 0) {
    e_0.preventDefault();
    return;
  }
  const active = document.activeElement;
  const inside = active instanceof Node && dialog.contains(active);
  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (e_0.shiftKey && (!inside || active === first)) {
    e_0.preventDefault();
    last.focus();
  } else {
    if (!e_0.shiftKey && (!inside || active === last)) {
      e_0.preventDefault();
      first.focus();
    }
  }
}
function _temp4(el_1) {
  return el_1.offsetParent !== null;
}
function _temp3(prev_0) {
  return !prev_0;
}
function _temp2(prev) {
  const next = window.scrollY > 20;
  return prev === next ? prev : next;
}
function _temp(s) {
  return s.id;
}